const asNumber = (value) => parseFloat(value || 0);

const REFUND_METHOD_LABELS = {
  cash: 'Cash',
  store_credit: 'Store Credit',
  same_payment_mode: 'Same Payment Mode',
};

const POLICY_ONLY_REFUND_METHODS = new Set(['cashier_decides']);

const toRefundMethodLabel = (value) => {
  if (!value) return '';
  if (POLICY_ONLY_REFUND_METHODS.has(value)) return '';
  return REFUND_METHOD_LABELS[value] || value;
};

const resolvePrintableRefundMethod = (exc, returnSide) => {
  // Only show refund method when the customer is actually receiving a refund
  if (exc.settlement_direction !== 'customer_refund') return '';

  const candidates = [
    returnSide.refund_method_name,
    returnSide.resolved_refund_method,
    returnSide.actual_refund_method,
    returnSide.refund_mode_name,
    returnSide.refund_mode,
    exc.refund_method_name,
    exc.resolved_refund_method,
    exc.actual_refund_method,
    exc.refund_mode_name,
    exc.refund_mode,
    exc.refund_method,
    returnSide.refund_method,
  ];

  for (const candidate of candidates) {
    const label = toRefundMethodLabel(candidate);
    if (label) return label;
  }

  return '';
};

const buildGstSummary = (items = []) => {
  const rows = new Map();
  items.forEach((item) => {
    const key = `${item.cgst_rate || 0}|${item.sgst_rate || 0}|${item.igst_rate || 0}`;
    const existing = rows.get(key) || {
      label:
        item.igst_rate && asNumber(item.igst_rate) > 0
          ? `IGST ${asNumber(item.igst_rate).toFixed(0)}%`
          : `GST ${(asNumber(item.cgst_rate) + asNumber(item.sgst_rate)).toFixed(0)}%`,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
    };
    const lineTotal = asNumber(item.line_total || item.refund_amount);
    const taxAmt = asNumber(item.tax_amount);
    existing.taxableAmount += Math.max(0, lineTotal - taxAmt);
    existing.cgstAmount += asNumber(item.cgst_amount);
    existing.sgstAmount += asNumber(item.sgst_amount);
    existing.igstAmount += asNumber(item.igst_amount);
    rows.set(key, existing);
  });
  return Array.from(rows.values()).filter(
    (r) => r.taxableAmount > 0 || r.cgstAmount > 0 || r.sgstAmount > 0 || r.igstAmount > 0,
  );
};

export const mapExchangeToReceipt = (payload, template) => {
  const exc = payload?.data?.exchange || payload?.exchange || payload;
  if (!exc || !exc.id) return null;

  const returnSide = exc.return_side || {};
  const newSaleSide = exc.new_sale_side || {};

  const returnItems = (returnSide.items || []).map((item, i) => ({
    index: i + 1,
    name: item.product_name || 'Item',
    hsn: item.hsn_code?.code || '',
    quantity: asNumber(item.quantity_returned),
    rate: asNumber(item.unit_price),
    amount: asNumber(item.refund_amount),
    reason: item.reason_name || '',
    cgst_rate: item.cgst_rate,
    sgst_rate: item.sgst_rate,
    igst_rate: item.igst_rate,
    cgst_amount: item.cgst_amount,
    sgst_amount: item.sgst_amount,
    igst_amount: item.igst_amount,
    tax_amount: item.tax_amount,
  }));

  const newItems = (newSaleSide.items || []).map((item, i) => ({
    index: i + 1,
    name: item.product_name || 'Item',
    hsn: item.hsn_code?.code || '',
    quantity: asNumber(item.quantity),
    rate: asNumber(item.unit_price),
    amount: asNumber(item.line_total),
    cgst_rate: item.cgst_rate,
    sgst_rate: item.sgst_rate,
    igst_rate: item.igst_rate,
    cgst_amount: item.cgst_amount,
    sgst_amount: item.sgst_amount,
    igst_amount: item.igst_amount,
    tax_amount: item.tax_amount,
  }));

  const payments = (newSaleSide.payments || []).map((p) => ({
    label: p.payment_mode_name || p.payment_mode || 'Payment',
    amount: asNumber(p.amount),
    reference: p.reference_number || '',
  }));

  return {
    exchangeId: exc.id,
    exchangeNumber: `EXC-${exc.id}`,
    status: exc.status,
    completedAt: exc.completed_at || exc.created_at,
    originalOrderNumber: exc.original_order_number || '',

    // Return side
    returnNumber: returnSide.return_number || '',
    creditNoteNumber: returnSide.credit_note_number || null,
    debitNoteNumber: returnSide.debit_note_number || null,
    // BXGY promo discount reversed on kept items (already deducted from return_total)
    promotionClawbackTotal: asNumber(returnSide.promotion_clawback_total ?? 0),
    returnTotal: asNumber(exc.return_total),
    refundMethod: resolvePrintableRefundMethod(exc, returnSide),
    returnItems,
    returnGstSummary: buildGstSummary(
      returnItems.map((i) => ({ ...i, refund_amount: i.amount, line_total: i.amount })),
    ),

    // New sale side
    newInvoiceNumber: newSaleSide.order_number || '',
    newSaleTotal: asNumber(exc.new_items_total),
    newItems,
    newSaleGstSummary: buildGstSummary(
      newItems.map((i) => ({ ...i, refund_amount: i.amount, line_total: i.amount })),
    ),
    payments,

    // Settlement
    netSettlementAmount: asNumber(exc.net_settlement_amount),
    settlementDirection: exc.settlement_direction || 'even',

    // Parties
    storeName: exc.store?.name || '',
    storePhone: exc.store?.phone_number || '',
    storeEmail: exc.store?.email || '',
    storeAddress: exc.store?.address || '',
    organizationName: exc.organization?.name || '',
    gstin: exc.organization?.gstin || '',
    cashierName: exc.cashier?.name || '',
    terminalName: exc.terminal?.name || '',
    customerName: exc.customer?.full_name || '',
    customerMobile: exc.customer?.phone_number || '',
    customerGstin: exc.customer?.gstin || '',

    // Branding (exchange receipts don't show Terms & Conditions -- pre-existing,
    // not something this pass adds; footer/logo/handles/declaration do apply though)
    footerMessage: template?.footer_message || '',
    websiteHandle: template?.website_handle || '',
    instagramHandle: template?.instagram_handle || '',
    declarationText: template?.declaration_text || '',
    logoUrl: template?.logo_url || '',
    rateColumnLabel: template?.rate_column_label || 'Rate',
    amountColumnLabel: template?.amount_column_label || 'Amt',
    showRateColumn: template?.show_rate_column !== false,
    showAmountColumn: template?.show_amount_column !== false,
    itemAmountDecimalPlaces: template?.item_amount_decimal_places || 2,
    receiptFontSize: template?.receipt_font_size || 'normal',
  };
};
